import React, { useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import styled, { useTheme } from 'styled-components'
import { Flex, Text, TextVariant, Button } from '@mprolab/uikit'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import {
  ProposalStatus,
  ExecutionRecordKind
} from '@mprolab/sdk'
import MainWrapper from 'layouts/MainWrapper'
import { useGetProposalQuery } from 'state/services/indexNode/indexNode'
import { useWeb3React } from 'lib/wagmi-compat/useWeb3React'
import { useLifetimeTokens } from 'state/global/hooks'
import { useGetMyMembershipQuery } from 'state/services/community/community'
import { MarkdownBody } from 'views/Documentation/MarkdownBody'
import VoteButtonRow from './components/VoteButtonRow'
import TallyBar from './components/TallyBar'
import ClanVoteCard from './components/ClanVoteCard'
import DecisionRegistryRecord from './components/DecisionRegistryRecord'

const Header = styled(Flex)`
  flex-direction: column;
  gap: 12px;
`

const TopBar = styled(Flex)`
  align-items: baseline;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
`

const StatusPill = styled.span<{ $bg: string; $color: string }>`
  padding: 4px 12px;
  border-radius: 999px;
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`

const Meta = styled(Flex)`
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 8px;
`

const Section = styled(Flex)`
  flex-direction: column;
  gap: 12px;
  padding: 16px 0;
  border-top: 1px solid ${({ theme }) => theme.colors.gray8};
`

const ProposalDetailView: React.FC = () => {
  const { proposalId } = useParams<{ proposalId: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { colors } = useTheme()

  // The session travels with the read, or the service cannot tell us how this
  // wallet voted and the buttons stay on screen after the ballot is counted.
  const { account } = useWeb3React()
  const lifetimeTokens = useLifetimeTokens()
  const accessToken = account ? lifetimeTokens?.[account] : undefined

  // Which ballot this person gets. Membership decides it, so it is read before
  // anything is rendered rather than inside the card that needs it.
  const membership = useGetMyMembershipQuery(
    { account: account as string, accessToken: accessToken as string },
    { skip: !account || !accessToken, refetchOnMountOrArgChange: true }
  )
  const inClan = Boolean(membership.data?.clan)

  const { data, isLoading } = useGetProposalQuery(
    { proposalId: proposalId ?? '', account: account ?? undefined, accessToken },
    { skip: !proposalId, refetchOnMountOrArgChange: true, pollingInterval: 30_000 }
  )

  const statusStyle = useMemo(() => {
    if (!data) return { bg: colors.gray8, color: colors.gray6 }
    switch (data.proposal.status) {
      case ProposalStatus.ACTIVE:
        return { bg: `${colors.cyan}33`, color: colors.cyan }
      case ProposalStatus.PASSED:
      case ProposalStatus.EXECUTED:
        return { bg: `${colors.green}33`, color: colors.green }
      case ProposalStatus.REJECTED:
        return { bg: `${colors.red}33`, color: colors.red }
      default:
        return { bg: colors.gray8, color: colors.gray6 }
    }
  }, [data, colors])

  if (isLoading || !data) {
    return (
      <MainWrapper>
        <Flex fullWidth justifyContent="center" minHeight={300} alignItems="center">
          <Text color={colors.gray6}>{t('dao.loading')}</Text>
        </Flex>
      </MainWrapper>
    )
  }

  const { proposal, tally, myVote, decisionLog } = data
  const proposer = `${proposal.proposerWallet.slice(0, 8)}…${proposal.proposerWallet.slice(-6)}`
  const opens = dayjs.unix(proposal.votingStartTimestamp).format('YYYY-MM-DD HH:mm UTC')
  const closes = dayjs.unix(proposal.votingEndTimestamp).format('YYYY-MM-DD HH:mm UTC')

  return (
    <MainWrapper>
      <Flex fullWidth flexDirection="column" mt="152px" mb="120px" gap="20px">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => navigate('/dao')}
        >
          ← {t('dao.backToList')}
        </Button>

        <Header>
          <TopBar>
            <Text variant={TextVariant.H4}>{proposal.title}</Text>
            <StatusPill $bg={statusStyle.bg} $color={statusStyle.color}>
              {t(`dao.status.${proposal.status}`)}
            </StatusPill>
          </TopBar>
          <Meta>
            <Text variant={TextVariant.BODY_SMALL} color={colors.gray6}>
              {t('dao.category.label')}: {t(`dao.category.${proposal.category}`)}
            </Text>
            <Text variant={TextVariant.BODY_SMALL} color={colors.gray6}>
              {t('dao.executionType.label')}: {t(`dao.executionType.${proposal.executionType}`)}
            </Text>
            <Text variant={TextVariant.BODY_SMALL} color={colors.gray6}>
              {t('dao.proposedBy')}: {proposer}
            </Text>
            <Text variant={TextVariant.BODY_SMALL} color={colors.gray6}>
              {t('dao.snapshotBlock')}: #{proposal.snapshotBlock}
            </Text>
          </Meta>
          <Meta>
            <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
              {t('dao.opensAt', { date: opens })}
            </Text>
            <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
              {t('dao.closesAt', { date: closes })}
            </Text>
          </Meta>
        </Header>

        <TallyBar tally={tally} params={proposal.params} />

        {/*
          One ballot, never two.
          
          A member of a clan votes inside their clan and the clan carries the
          weight; the DAO refuses a personal vote from them. Showing both
          controls was the confusion this replaces — two ways to vote on one
          screen, one of which the server would reject with a conflict the
          voter read as a fault of theirs.
        */}
        {proposal.status === ProposalStatus.ACTIVE ? (
          inClan ? (
            <ClanVoteCard proposalId={proposalId ?? ''} />
          ) : (
            <VoteButtonRow proposal={proposal} myVote={myVote} />
          )
        ) : (
          <ClanVoteCard proposalId={proposalId ?? ''} />
        )}

        <Section>
          <Text variant={TextVariant.BODY_DEFAULT_BOLD}>{t('dao.body')}</Text>
          <MarkdownBody markdown={proposal.bodyMarkdown} outline={[]} />
        </Section>

        {proposal.status === ProposalStatus.EXECUTED && proposal.executionRecord ? (
          <Section>
            <Text variant={TextVariant.BODY_DEFAULT_BOLD}>
              {t('dao.executionRecord.title')}
            </Text>
            <DecisionRegistryRecord
              record={proposal.executionRecord}
              kindLabel={t(
                `dao.executionKind.${proposal.executionRecord.executionKind as ExecutionRecordKind}`
              )}
            />
          </Section>
        ) : null}

        <Section>
          <Text variant={TextVariant.BODY_DEFAULT_BOLD}>{t('dao.timeline')}</Text>
          <Flex flexDirection="column" gap="6px">
            {decisionLog.map((entry) => (
              <Flex key={entry.logId} gap="10px" alignItems="baseline">
                <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
                  {dayjs(entry.occurredAt).format('YYYY-MM-DD HH:mm UTC')}
                </Text>
                <Text variant={TextVariant.BODY_SMALL_BOLD}>
                  {t(`dao.timelineEvent.${entry.event}`)}
                </Text>
                {entry.payload ? (
                  <Text variant={TextVariant.BODY_SMALL} color={colors.gray6}>
                    — {entry.payload}
                  </Text>
                ) : null}
              </Flex>
            ))}
          </Flex>
        </Section>
      </Flex>
    </MainWrapper>
  )
}

export default ProposalDetailView
